<template>
  <div
    class="editorPanel tw-left-2 tw-right-2 lg:tw-left-auto tw-bg-white tw-absolute tw-bottom-0 tw-transition tw-ease-out tw-duration-500 tw-rounded-t-[24px] lg:tw-rounded-tr-none lg:tw-rounded-l-[24px] lg:tw-w-[400px] lg:tw-right-0 lg:tw-bottom-auto lg:tw-top-1/2 lg:tw-translate-y-[-50%] tw-font-sans tw-z-[3] tw-overflow-hidden"
    :class="{ panelHidden: !showPanel }"
    :style="panelStyle"
  >
    <div ref="panelContentRef" class="tw-flex tw-flex-col tw-overflow-y-auto">
      <div class="textOptions tw-flex tw-justify-between tw-items-center tw-p-4 tw-px-8">
        <img
          :src="getAssetPath('/assets/images/collapseIcon.svg')"
          :alt="$t('closeButton')"
          @click="$emit('update:showPanel', false)"
          class="tw-cursor-pointer"
        />
      </div>

      <div
        v-if="previouslyUploadedImages.length"
        class="tw-overflow-x-auto tw-whitespace-nowrap tw-mx-8 tw-mb-4 tw-flex tw-p-2 tw-rounded-md"
        :class="scrollbarClasses"
      >
        <div
          v-for="(file, index) of previouslyUploadedImages"
          :key="index"
          class="image-container tw-cursor-pointer"
          @click="$emit('onImageSelect', file.image)"
        >
          <img
            class="previousImage"
            :src="file.image"
            :alt="truncate(file.fileName, 15)"
          />
          <span class="filename tw-text-white">{{ truncate(file.fileName, 15) }}</span>
        </div>
      </div>

      <div class="tw-px-8">
        <div
          class="tw-rounded-l-full tw-rounded-r-full tw-bg-bgDark tw-border-solYellow tw-border-1 tw-border-solid tw-flex tw-justify-center tw-items-center tw-text-xl tw-p-4 tw-mb-4 tw-text-white tw-font-semibold tw-cursor-pointer"
          @click.stop="onAddImage"
        >
          <span class="tw-flex tw-justify-center tw-items-center">
            <img
              :src="getAssetPath('/assets/images/uploadBackground.svg')"
              :alt="$t('uploadBackground')"
              class="tw-mr-2"
            />
            {{ $t("uploadBackground") }}
          </span>
        </div>
      </div>

      <input
        ref="inputRef"
        type="file"
        accept="image/jpeg, image/png, image/svg+xml, image/webp"
        class="tw-hidden"
        @change="onFileUpload"
      />
    </div>
  </div>
</template>

<script>
import resizeImage from "@/utils/helpers/resizeImage";
import readFileAsDataURL from "@/utils/helpers/readFileAsDataURL";
import convertSvgToPng from "@/utils/helpers/convertSvgToPng";
import { getAssetPath } from "@/utils/assetPath";
import { scrollbarClasses } from "@/utils/tailwindScrollbarClasses";

export default {
  setup() {
    return {
      getAssetPath,
      scrollbarClasses,
    };
  },
  props: {
    showPanel: {
      required: true,
      type: Boolean,
    },
  },
  emits: ["update:showPanel", "onImageSelect"],
  computed: {
    panelStyle() {
      const maxH = (typeof window !== "undefined" ? window.innerHeight : 800) * 0.64;
      const transformTransition = "transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)";
      const heightTransition = this.panelHeight ? ", height 0.4s ease-out" : "";
      return {
        height: this.panelHeight ? `${this.panelHeight}px` : "auto",
        maxHeight: `${maxH}px`,
        transition: transformTransition + heightTransition,
      };
    },
  },
  watch: {
    showPanel(val) {
      if (val && !this.previouslyUploadedImages.length) {
        this.$nextTick(() => {
          this.updatePanelHeight();
          if (this.$refs.panelContentRef && !this.resizeObserver) {
            this.resizeObserver = new ResizeObserver(() => this.updatePanelHeight());
            this.resizeObserver.observe(this.$refs.panelContentRef);
          }
          this.onAddImage();
        });
        return;
      }
      if (val) {
        this.$nextTick(() => {
          this.updatePanelHeight();
          if (this.$refs.panelContentRef && !this.resizeObserver) {
            this.resizeObserver = new ResizeObserver(() => this.updatePanelHeight());
            this.resizeObserver.observe(this.$refs.panelContentRef);
          }
        });
      }
    },
  },
  mounted() {
    if (this.showPanel) {
      this.$nextTick(() => {
        this.updatePanelHeight();
        if (this.$refs.panelContentRef && !this.resizeObserver) {
          this.resizeObserver = new ResizeObserver(() => this.updatePanelHeight());
          this.resizeObserver.observe(this.$refs.panelContentRef);
        }
      });
    }
  },
  beforeUnmount() {
    if (this.resizeObserver && this.$refs.panelContentRef) {
      this.resizeObserver.unobserve(this.$refs.panelContentRef);
    }
  },
  methods: {
    updatePanelHeight() {
      if (!this.$refs.panelContentRef || !this.showPanel) return;
      const el = this.$refs.panelContentRef;
      const maxPx = (window.innerHeight * 64) / 100;
      this.panelHeight = Math.min(el.scrollHeight, maxPx);
    },
    truncate(string, position) {
      if (string && string.length > position)
        return string.substring(0, position) + "...";
      return string;
    },
    onAddImage() {
      this.$refs.inputRef.click();
    },
    async onFileUpload(event) {
      const file = event.target.files[0];
      if (!file) return;
      let fileName = file.name;
      let image;

      if (file.type.includes("svg")) {
        const svgDataUrl = await readFileAsDataURL(file);
        image = await convertSvgToPng(svgDataUrl);
        fileName = fileName.replace(/\.svg$/i, ".png");
      } else {
        image = await resizeImage(file, 1280, 1280);
      }

      this.previouslyUploadedImages.push({ image, fileName });
      this.$emit("onImageSelect", image);
      this.$refs.inputRef.value = "";
      this.$nextTick(() => this.updatePanelHeight());
    },
  },
  data() {
    return {
      previouslyUploadedImages: [],
      panelHeight: 0,
      resizeObserver: null,
    };
  },
};
</script>

<style lang="scss">
#cardDesigner {
  .previousImage {
    max-width: unset;
    height: 130px;
    border-radius: 0.75rem;
    border: 1px solid #eee;
    display: inline-block;
    margin-right: 1rem;
    margin-top: 4px;
  }

  .image-container {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .filename {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    text-align: center;
    width: 100%;
    margin-top: 5px;
    font-size: 13px;
  }
}
</style>
